import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface ChatMessage {
    text: string;
    isUser: boolean; // true = user, false = bot
}

export interface ChatRequest {
    session_id: string;
    prompt: string;
}

export interface ChatResponse {
    body: string;
}

export interface ParsedChatResponse {
    briefAnswer: string;
    // Add other properties from your API response here
}

@Injectable({
    providedIn: 'root',
})
export class ChatService {
    private readonly apiUrl = 'https://irrgieo6jf.execute-api.us-west-2.amazonaws.com/dev/agent';
    private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);

    constructor(private http: HttpClient) {}

    /**
     * Send a chat message to the API
     * @param sessionId - The session identifier
     * @param message - The user's message/prompt
     * @returns Observable with parsed chat response
     */
    sendMessage(sessionId: string, message: string): Observable<ParsedChatResponse> {
        const payload: ChatRequest = {
            session_id: sessionId,
            prompt: message,
        };

        return this.http.post<ChatResponse>(this.apiUrl, payload).pipe(
            map((response) => {
                // Parse the JSON body from the API response
                const parsedBody = JSON.parse(response.body);
                return {
                    briefAnswer: parsedBody.briefAnswer || 'No response from API',
                    ...parsedBody,
                };
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Handle HTTP errors
     * @param error - The HTTP error response
     * @returns Observable that throws a user-friendly error
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An error occurred while processing your request.';

        if (error.error instanceof ErrorEvent) {
            // Client-side or network error
            console.error('Client-side error:', error.error.message);
            errorMessage = 'Network error. Please check your connection.';
        } else {
            // Backend returned an unsuccessful response code
            console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);

            switch (error.status) {
                case 400:
                    errorMessage = 'Invalid request. Please try again.';
                    break;
                case 401:
                    errorMessage = 'Unauthorized access. Please log in.';
                    break;
                case 500:
                    errorMessage = 'Server error. Please try again later.';
                    break;
                default:
                    errorMessage = 'Sorry, I encountered an error. Please try again.';
            }
        }

        return throwError(() => new Error(errorMessage));
    }

    addMessage(msg: ChatMessage) {
        const currentMessages = this.messagesSubject.value;
        this.messagesSubject.next([...currentMessages, msg]);
    }

    getMessages(): Observable<ChatMessage[]> {
        return this.messagesSubject.asObservable();
    }

    clearMessages() {
        this.messagesSubject.next([]);
    }
}
